import React from "react";
import Moment from "moment";
import { t, Download } from "../normalizer";
import CustomButton from "../shared/CustomButton";

// Read-only voucher metadata card. Renders the available fields in a 4-column
// CSS Grid (collapses responsively) plus a direct Download action. Reads every
// field defensively so it tolerates a partial API payload.
const VoucherDetail = ({ voucher }) => {
  if (!voucher) return null;

  const formatDate = (value) =>
    value ? new Moment(value).format(t("date-format")) : "-";

  // The user vouchers endpoint nests most display fields under `voucher_type`.
  // Read the nested structure with fallbacks to legacy flat keys so the detail
  // view tolerates every voucher payload shape.
  const voucherType = voucher.voucher_type || {};
  const product = voucherType.product || {};
  const category = voucherType.category || voucher.category || {};
  const state = voucher.state || {};
  const order = voucher.order || {};

  const title =
    voucherType.name || product.name || voucher.name || voucher.description || "-";

  // The Estado field renders a coloured pill (styled off state.code) instead of
  // plain text, so it is expressed as a `node` rather than a `value`.
  const stateCode = state.code || "";
  const stateLabel = state.label || state.code || voucher.status_label || voucher.status;
  const stateNode = stateLabel
    ? (
      <span className={stateCode ? "voucher-state-pill voucher-state-pill--" + stateCode : "voucher-state-pill"}>
        {stateLabel}
      </span>
    )
    : null;

  // Each entry is rendered only when its value/node is present, so the grid
  // never shows empty cells for fields the backend omits.
  const fields = [
    // Row 1
    { label: t("gamification2026.voucher.fields.order-number"), value: order.number },
    { label: t("gamification2026.voucher.fields.expiry"), value: formatDate(voucher.validity || voucher.expires_at) },
    { label: t("gamification2026.voucher.fields.date"), value: formatDate(voucher.purchased_at || voucher.created_at || voucher.date) },
    { label: t("gamification2026.voucher.fields.status"), node: stateNode },
    // Row 2
    { label: t("gamification2026.voucher.fields.email"), value: order.email },
    { label: t("gamification2026.voucher.fields.phone"), value: order.phone },
    { label: t("gamification2026.voucher.fields.points"), value: order.points !== undefined && order.points !== null ? order.points : voucher.points },
    { label: voucherType.number_label || t("gamification2026.voucher.fields.code"), value: voucher.number || voucher.code || voucher.voucher_code },
  ].filter((field) =>
    field.node
      ? true
      : field.value !== undefined && field.value !== null && field.value !== ""
  );

  return (
    <div className="voucher-detail">
      <div className="voucher-detail-header">
        <h5 className="voucher-detail-header-title">{title}</h5>
      </div>

      <div className="voucher-detail-grid">
        {fields.map((field, index) => (
          <div className="voucher-detail-grid-item" key={index}>
            <span className="voucher-detail-grid-label">{field.label}</span>
            <span className="voucher-detail-grid-value">{field.node || field.value}</span>
          </div>
        ))}
      </div>

      <div className="voucher-detail-actions">
        {voucher.url ? (
          <CustomButton
            link
            to={voucher.url}
            target="_blank"
            className="voucher-detail-download"
          >
            <Download className="voucher-detail-download-icon" />
            {t("gamification2026.voucher.download")}
          </CustomButton>
        ) : (
          <CustomButton
            type="button"
            disabled
            className="voucher-detail-download"
          >
            <Download className="voucher-detail-download-icon" />
            {t("gamification2026.voucher.download")}
          </CustomButton>
        )}
      </div>
    </div>
  );
};

export default VoucherDetail;
